import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Heart, Briefcase, ShoppingBag, Stethoscope, SignOut, User, GraduationCap, GameController, Coin, Receipt, Translate, ShieldCheck, HandHeart, Scales, Confetti, Printer, Ticket } from "@phosphor-icons/react";
import Logo from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang, LANG_OPTIONS } from "@/context/LangContext";

const NAV = [
  { to: "/matrimony", key: "nav.matrimony", icon: Heart },
  { to: "/jobs", key: "nav.jobs", icon: Briefcase },
  { to: "/marketplace", key: "nav.marketplace", icon: ShoppingBag },
  { to: "/training", key: "nav.training", icon: GraduationCap },
  { to: "/doctors", key: "nav.doctors", icon: Stethoscope },
  { to: "/lawyers", key: "nav.lawyers", icon: Scales },
  { to: "/events", key: "nav.events", icon: Confetti },
  { to: "/support", key: "nav.support", icon: HandHeart },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [credits, setCredits] = useState(null);
  const { lang, setLang, t } = useLang();

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!user || user === false) { if (mounted) setCredits(null); return; }
      try {
        const { data } = await api.get("/wallet");
        if (mounted) setCredits(data.credits);
      } catch { if (mounted) setCredits(null); }
    };
    load();
    const int = setInterval(load, 15000);
    return () => { mounted = false; clearInterval(int); };
  }, [user, loc.pathname]);

  return (
    <div className="min-h-screen" style={{ background: "#F9F6F0" }}>
      <header
        data-testid="site-header"
        className="sticky top-0 z-40 border-b border-[#E5D9CC]"
        style={{ background: "rgba(249, 246, 240, 0.94)", backdropFilter: "blur(10px)" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <Link to="/" data-testid="nav-logo" className="shrink-0"><Logo size={34} /></Link>

          <nav className="hidden lg:flex items-center gap-0.5">
            {NAV.map((n) => {
              const Icon = n.icon;
              const active = loc.pathname.startsWith(n.to);
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.key.split(".")[1]}`}
                  className="px-3 py-2 rounded-full text-sm font-semibold flex items-center gap-2 whitespace-nowrap"
                  style={{
                    color: active ? "#F9F6F0" : "#2C4A3B",
                    background: active ? "#2C4A3B" : "transparent",
                    transition: "background-color 180ms ease-out, color 180ms ease-out",
                  }}
                >
                  <Icon size={16} weight={active ? "fill" : "regular"} />
                  {t(n.key)}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            <div className="relative flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-[#E5D9CC] bg-[#F2EBE5]">
              <Translate size={12} weight="bold" className="text-[#2C4A3B]" />
              <select
                data-testid="lang-toggle"
                value={lang}
                onChange={(e) => setLang(e.target.value)}
                className="bg-transparent text-xs font-bold text-[#2C4A3B] outline-none pr-1"
                aria-label="Select language"
              >
                {LANG_OPTIONS.map((l) => (<option key={l.code} value={l.code}>{l.label}</option>))}
              </select>
            </div>
            {user && user !== false ? (
              <>
                {credits !== null && (
                  <div data-testid="wallet-credits" className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F2EBE5] border border-[#E5D9CC] text-xs font-bold text-[#D49A36]">
                    <Coin size={14} weight="fill" /> {credits}
                  </div>
                )}
                <NotificationBell />
                <Link to="/orders" data-testid="nav-orders" className="hidden sm:flex w-9 h-9 rounded-full items-center justify-center border border-[#E5D9CC] bg-[#F2EBE5] hover:bg-[#EFE4D3]" style={{ transition: "background-color 180ms ease-out" }} title={t("nav.orders")}>
                  <Receipt size={16} className="text-[#2C4A3B]" />
                </Link>
                {(user.role === "admin" || user.role === "manager") && (
                  <Link to="/admin" data-testid="nav-admin" className="hidden sm:flex w-9 h-9 rounded-full items-center justify-center border border-[#E5D9CC] bg-[#F2EBE5] hover:bg-[#EFE4D3]" style={{ transition: "background-color 180ms ease-out" }} title="Admin">
                    <ShieldCheck size={16} className="text-[#D95A41]" weight="fill" />
                  </Link>
                )}
                <span data-testid="user-name" className="hidden md:flex items-center gap-2 text-sm text-[#5C564D]">
                  <User size={16} /> {user.name}
                  {user.user_type && user.user_type !== "user" && <span className="samaaj-chip text-[10px] uppercase">{user.user_type}</span>}
                </span>
                <RoleSwitcher />
                <button
                  data-testid="btn-logout"
                  onClick={async () => { await logout(); nav("/"); }}
                  className="samaaj-btn-outline text-sm flex items-center gap-2"
                >
                  <SignOut size={16} /> {t("nav.logout")}
                </button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="btn-login-nav" className="samaaj-btn-outline text-sm">{t("nav.login")}</Link>
                <Link to="/register" data-testid="btn-register-nav" className="samaaj-btn-primary text-sm">{t("nav.join")}</Link>
              </>
            )}
          </div>
        </div>

        {/* Mobile / tablet nav */}
        <div className="lg:hidden border-t border-[#E5D9CC] px-2 py-2 overflow-x-auto flex gap-1">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = loc.pathname.startsWith(n.to);
            return (
              <Link
                key={n.to}
                to={n.to}
                className="px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 whitespace-nowrap shrink-0"
                style={{
                  color: active ? "#F9F6F0" : "#2C4A3B",
                  background: active ? "#2C4A3B" : "#F2EBE5",
                }}
              >
                <Icon size={14} /> {t(n.key)}
              </Link>
            );
          })}
        </div>
      </header>

      <main>{children}</main>

      <footer className="mt-24 border-t border-[#E5D9CC]" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-6 py-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-2"><Logo size={28} /><span className="font-heading text-lg text-[#2C4A3B]">Anveshan · अन्वेषण</span></div>
            <p className="mt-3 text-sm text-[#5C564D] max-w-xs leading-relaxed">
              {lang === "hi"
                ? "संभावनाओं की खोज, अवसरों का सृजन, समाज का उत्थान।"
                : "Discovering potential, creating opportunities, empowering society."}
            </p>
          </div>
          <div>
            <div className="text-xs tracking-widest uppercase font-bold text-[#2C4A3B]">{lang === "hi" ? "कंपनी" : "Company"}</div>
            <ul className="mt-3 space-y-1.5 text-sm text-[#5C564D]">
              <li><Link to="/discover" data-testid="f-about" className="hover:text-[#2C4A3B]">{lang === "hi" ? "परिचय" : "About"}</Link></li>
              <li><Link to="/support" data-testid="f-contact" className="hover:text-[#2C4A3B]">{lang === "hi" ? "संपर्क" : "Contact"}</Link></li>
              <li><Link to="/support" data-testid="f-help" className="hover:text-[#2C4A3B]">{lang === "hi" ? "सहायता" : "Help & FAQs"}</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs tracking-widest uppercase font-bold text-[#2C4A3B]">{lang === "hi" ? "क़ानूनी" : "Legal"}</div>
            <ul className="mt-3 space-y-1.5 text-sm text-[#5C564D]">
              <li><a href="#" data-testid="f-privacy" className="hover:text-[#2C4A3B]">{lang === "hi" ? "निजता नीति" : "Privacy Policy"}</a></li>
              <li><a href="#" data-testid="f-terms" className="hover:text-[#2C4A3B]">{lang === "hi" ? "शर्तें" : "Terms"}</a></li>
              <li><a href="#" data-testid="f-guidelines" className="hover:text-[#2C4A3B]">{lang === "hi" ? "समुदाय दिशानिर्देश" : "Community Guidelines"}</a></li>
            </ul>
          </div>
          <div>
            <div className="text-xs tracking-widest uppercase font-bold text-[#2C4A3B]">{lang === "hi" ? "सेवाएँ" : "Services"}</div>
            <ul className="mt-3 space-y-1.5 text-sm text-[#5C564D] grid grid-cols-2 gap-x-3">
              <li><Link to="/matrimony" className="hover:text-[#2C4A3B]">{lang === "hi" ? "विवाह" : "Matrimony"}</Link></li>
              <li><Link to="/jobs" className="hover:text-[#2C4A3B]">{lang === "hi" ? "रोज़गार" : "Jobs"}</Link></li>
              <li><Link to="/doctors" className="hover:text-[#2C4A3B]">{lang === "hi" ? "डॉक्टर" : "Doctors"}</Link></li>
              <li><Link to="/training" className="hover:text-[#2C4A3B]">{lang === "hi" ? "प्रशिक्षण" : "Training"}</Link></li>
              <li><Link to="/marketplace" className="hover:text-[#2C4A3B]">{lang === "hi" ? "बाज़ार" : "Marketplace"}</Link></li>
              <li><Link to="/lawyers" className="hover:text-[#2C4A3B]">{lang === "hi" ? "वक़ील" : "Lawyers"}</Link></li>
              <li><Link to="/events" className="hover:text-[#2C4A3B]">{lang === "hi" ? "आयोजन" : "Events"}</Link></li>
              <li><Link to="/support" className="hover:text-[#2C4A3B]">{lang === "hi" ? "सहायता" : "Support"}</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-[#E5D9CC] py-5 text-center text-xs text-[#8A8378]">
          © {new Date().getFullYear()} Anveshan Society · {lang === "hi" ? "सभी अधिकार सुरक्षित" : "All rights reserved"}
        </div>
      </footer>
    </div>
  );
}

const USER_TYPES = [
  { id: "user", label: "General user" },
  { id: "employer", label: "Employer" },
  { id: "seller", label: "Seller" },
  { id: "teacher", label: "Teacher" },
  { id: "organizer", label: "Event organizer" },
  { id: "doctor", label: "Doctor" },
  { id: "lawyer", label: "Lawyer" },
];

function RoleSwitcher() {
  const { user, changeUserType } = useAuth();
  const [open, setOpen] = useState(false);
  if (!user || user === false) return null;
  const change = async (t) => {
    try { await changeUserType(t); setOpen(false); } catch (err) { console.error("change user type failed", err); }
  };
  return (
    <div className="relative">
      <button data-testid="btn-change-role" onClick={() => setOpen((o) => !o)} className="hidden sm:flex w-9 h-9 rounded-full items-center justify-center border border-[#E5D9CC] bg-[#F2EBE5] hover:bg-[#EFE4D3]" title="Change my role">
        <User size={16} className="text-[#2C4A3B]" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-[#F9F6F0] border border-[#E5D9CC] rounded-2xl shadow-lg p-2 z-50">
          <div className="text-[10px] uppercase tracking-widest text-[#5C564D] px-2 py-1">Register as</div>
          {USER_TYPES.map((t) => (
            <button key={t.id} onClick={() => change(t.id)} data-testid={`role-opt-${t.id}`} className={`w-full text-left px-2 py-1.5 rounded-lg text-sm ${user.user_type === t.id ? "bg-[#2C4A3B] text-[#F9F6F0]" : "hover:bg-[#F2EBE5] text-[#1C1B1A]"}`}>
              {t.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
